(ns admin.events.questionnaires
  (:require
   [re-frame.core :as re-frame]

   [kyber.events.utils :refer [add-auth base-uri kyber-event-fx]]
   [kyber.lib.kyber-db :as kyber-db]
   [kyber.kyber-db.assessment :as assessment]

   [admin.utils :as au]
   [admin.router.utils :as aru]))

(defn- questionnaire-type->questionnaire-name
  [questionnaire-type]
  (case questionnaire-type
    :assessments "Assessment"
    :evaluation "Evaluation"
    :floating "Questionnaire"

    "Questionnaire"))

(defn- update-questionnaire-questions
  "Lens for updating questionnaire questions."
  [db {:keys [questionnaire-type questionnaire-uuid]} update-fn]
  (if (and (= questionnaire-type :floating)
           (some? questionnaire-uuid))
    (update-in db
               [:questionnaires questionnaire-type questionnaire-uuid]
               #(update % :questions update-fn))
    (update-in db
               [:questionnaires questionnaire-type :questions]
               update-fn)))

;; Urls

(defn- questionnaires-path
  ([activity-uuid]
   (str "/v2/admin/activities/" activity-uuid "/questionnaires-v2"))
  ([activity-uuid questionnaire-type]
   (str (questionnaires-path activity-uuid)
        "/" (name questionnaire-type))))

(defn- question-contexts-path
  ([activity-uuid questionnaire-type]
   (str (questionnaires-path activity-uuid questionnaire-type)
        "/question-contexts"))
  ([activity-uuid questionnaire-type question-context-uuid]
   (str (question-contexts-path activity-uuid questionnaire-type)
        "/" question-context-uuid)))

(defn- question-order-path
  [activity-uuid questionnaire-type]
  (str (questionnaires-path activity-uuid questionnaire-type)
       "/question-order"))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; get-questionnaires
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-get-questionnaires-success
    {:description "Get questionnaires success"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn- s-questionnaires->c-questionnaires
  [{:keys [floating] :as questionnaires}]
  (if (some? floating)
    (update questionnaires :floating
            (partial reduce
                     (fn [acc [q-uuid q]]
                       (assoc acc (name q-uuid) q))
                     {}))
    questionnaires))

(defn get-questionnaires-success
  [{:keys [db]} [event-name response]]
  {:db (assoc db :questionnaires (s-questionnaires->c-questionnaires response))
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx :questionnaires/get-questionnaires-success
                get-questionnaires-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-questionnaires-failure
    {:description "Get questionnaires failure"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-questionnaires-failure
  [{:keys [db]} [event-name _err]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx :questionnaires/get-questionnaires-failure
                get-questionnaires-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-questionnaires
    {:description "Get questionnaires request"
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn get-questionnaires
  [{:keys [db]} [event-name {:keys [activity-uuid]}]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :request
   {:name event-name
    :method :get
    :uri (base-uri db (questionnaires-path activity-uuid))
    :response-format :json
    :headers (add-auth db)
    :on-success [:questionnaires/get-questionnaires-success]
    :on-failure [:questionnaires/get-questionnaires-failure]}})

(kyber-event-fx :questionnaires/get-questionnaires get-questionnaires)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; create-questionnaire
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-create-questionnaire-success
    {:description "Create questionnaire success"
     :changelog
     [{:date "28MAY19"
       :event-version 0
       :data {}}]}))

(defn create-questionnaire-success
  [{:keys [db]} [event-name {:keys [questionnaire-type]} response]]
  {:db (if (= questionnaire-type :floating)
         (assoc-in db [:questionnaires questionnaire-type (:uuid response)]
                   response)
         (assoc-in db [:questionnaires questionnaire-type] response))
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast
                 {:message (str (questionnaire-type->questionnaire-name
                                 questionnaire-type)
                                " created.")}]]})

(kyber-event-fx :questionnaires/create-questionnaire-success
                create-questionnaire-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-create-questionnaire-failure
    {:description "Create questionnaire failure"
     :changelog
     [{:date "28MAY19"
       :event-version 0
       :data {}}]}))

(defn create-questionnaire-failure
  [{:keys [db]} [event-name questionnaire-type _]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast
                 {:status :error
                  :message (str "Failed to create your "
                                (questionnaire-type->questionnaire-name
                                 questionnaire-type)
                                ".")}]]})

(kyber-event-fx :questionnaires/create-questionnaire-failure
                create-questionnaire-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-create-questionnaire
    {:description "Create questionnaire request"
     :changelog
     [{:date "28MAY19"
       :event-version 0
       :data {}}]}))

(defn create-questionnaire
  [{:keys [db]} [event-name
                 {:keys [activity-uuid curriculum-uuid questionnaire-type]
                  :as questionnaire}]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :request
   {:name event-name
    :method :post
    :uri (base-uri db (questionnaires-path activity-uuid
                                           questionnaire-type))
    :response-format :json
    :format :json
    :headers (add-auth db)
    :params (cond-> {}
              (= questionnaire-type :floating)
              (assoc :name (:name questionnaire)))
    :on-success [:questionnaires/create-questionnaire-success
                 {:questionnaire-type questionnaire-type
                  :activity-uuid activity-uuid
                  :curriculum-uuid curriculum-uuid}]
    :on-failure [:questionnaires/create-questionnaire-failure
                 questionnaire-type]}})

(kyber-event-fx :questionnaires/create-questionnaire create-questionnaire)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; update-questionnaire
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-update-questionnaire-success
    {:description "Update questionnaire success"
     :changelog
     [{:date "28MAY19"
       :event-version 0
       :data {}}]}))

(defn update-questionnaire-success
  [{:keys [db]} [event-name
                 {:keys [questionnaire-type questionnaire-uuid]}
                 response]]
  {:db (update-in db
                  (if (= questionnaire-type :floating)
                    [:questionnaires questionnaire-type questionnaire-uuid]
                    [:questionnaires questionnaire-type])
                  merge response)
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast
                 {:message (str (questionnaire-type->questionnaire-name
                                 questionnaire-type)
                                " updated.")}]]})

(kyber-event-fx :questionnaires/update-questionnaire-success
                update-questionnaire-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-update-questionnaire-failure
    {:description "Update questionnaire failure"
     :changelog
     [{:date "28MAY19"
       :event-version 0
       :data {}}]}))

(defn update-questionnaire-failure
  [{:keys [db]} [event-name questionnaire-type _]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast
                 {:status :error
                  :message (str "Failed to update your "
                                (questionnaire-type->questionnaire-name
                                 questionnaire-type)
                                ".")}]]})

(kyber-event-fx :questionnaires/update-questionnaire-failure
                update-questionnaire-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-update-questionnaire
    {:description "Update questionnaire request"
     :changelog
     [{:date "28MAY19"
       :event-version 0
       :data {}}]}))

(defn update-questionnaire
  [{:keys [db]} [event-name {:keys [activity-uuid
                                    questionnaire-type
                                    questionnaire
                                    questionnaire-uuid]}]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :request
   {:name event-name
    :method :patch
    :uri (base-uri db (questionnaires-path
                       activity-uuid
                       (if (= questionnaire-type :floating)
                         questionnaire-uuid questionnaire-type)))
    :format :json
    :response-format :json
    :params questionnaire
    :headers (add-auth db)
    :on-success [:questionnaires/update-questionnaire-success
                 {:questionnaire-type questionnaire-type
                  :questionnaire-uuid questionnaire-uuid}]
    :on-failure [:questionnaires/update-questionnaire-failure
                 questionnaire-type]}})

(kyber-event-fx :questionnaires/update-questionnaire update-questionnaire)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; delete-questionnaire
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-delete-questionnaire-success
    {:description "Delete questionnaire success"
     :changelog
     [{:date "28MAY19"
       :event-version 0
       :data {}}]}))

(defn delete-questionnaire-success
  [{:keys [db]} [event-name {:keys [questionnaire-type]} _]]
  (let [{:keys [curriculum-uuid activity-uuid]}
        (get-in db [:router :route-params])
        nav-to-url
        (case questionnaire-type
          :assessments (aru/admin-path :activity-assessments
                                       :curriculum-uuid curriculum-uuid
                                       :activity-uuid activity-uuid
                                       :question-step "questions")
          :evaluation (aru/admin-path :activity-evaluation
                                      :curriculum-uuid curriculum-uuid
                                      :activity-uuid activity-uuid
                                      :question-step "questions")
          (js/console.error "Invalid questionnaire type"))]
    {:db (update-in db [:questionnaires] dissoc questionnaire-type)
     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0}]
                  [:router/nav-to nav-to-url]
                  [:notifier/create-toast
                   {:message (str
                              (questionnaire-type->questionnaire-name
                               questionnaire-type)
                              " removed.")}]]}))

(kyber-event-fx :questionnaires/delete-questionnaire-success
                delete-questionnaire-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-delete-questionnaire-failure
    {:description "Delete questionnaire failure"
     :changelog
     [{:date "28MAY19"
       :event-version 0
       :data {}}]}))

(defn delete-questionnaire-failure
  [{:keys [db]} [event-name _]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast
                 {:status :error
                  :message (str "Failed to delete, please try again.")}]]})

(kyber-event-fx :questionnaires/delete-questionnaire-failure
                delete-questionnaire-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-delete-questionnaire
    {:description "Delete questionnaire request"
     :changelog
     [{:date "28MAY19"
       :event-version 0
       :data {}}]}))

(defn delete-questionnaire
  [{:keys [db]} [event-name {:keys [activity-uuid
                                    questionnaire-type
                                    questionnaire-uuid]}]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :request
   {:name event-name
    :method :delete
    :uri (base-uri db (questionnaires-path
                       activity-uuid
                       (if (= questionnaire-type :floating)
                         questionnaire-uuid questionnaire-type)))
    :format :json
    :params {}
    :response-format :json
    :headers (add-auth db)
    :on-success [:questionnaires/delete-questionnaire-success
                 {:questionnaire-type questionnaire-type
                  :questionnaire-uuid questionnaire-uuid}]
    :on-failure [:questionnaires/delete-questionnaire-failure]}})

(kyber-event-fx :questionnaires/delete-questionnaire delete-questionnaire)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; create-question-context
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-create-question-context-success
    {:description "Create question success"
     :changelog
     [{:date "28MAY19"
       :event-version 0
       :data {}}]}))

(defn create-question-context-success
  [{:keys [db]} [event-name
                 {:keys [questionnaire-type questionnaire-uuid on-success]}
                 question-context]]
  (cond-> {:db (update-questionnaire-questions
                db
                {:questionnaire-type questionnaire-type
                 :questionnaire-uuid questionnaire-uuid}
                #(conj % question-context))
           :dispatch-n [[:analytics/save-datapoint {:event event-name
                                                    :event-version 0}]
                        [:notifier/create-toast
                         {:message "Question added!"}]]}
    (some? on-success)
    (assoc :invoke #(on-success question-context))))

(kyber-event-fx :questionnaires/create-question-context-success
                create-question-context-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-create-question-context-failure
    {:description "Create question failure"
     :changelog
     [{:date "28MAY19"
       :event-version 0
       :data {}}]}))

(defn create-question-context-failure
  [{:keys [db]} [event-name questionnaire-type _]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast
                 {:status :error
                  :message (str "Failed to create "
                                (name questionnaire-type)
                                " question.")}]]})

(kyber-event-fx :questionnaires/create-question-context-failure
                create-question-context-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-create-question-context
    {:description "Create question request"
     :changelog
     [{:date "28MAY19"
       :event-version 0
       :data {}}]}))

(defn create-question-context
  [{:keys [db]} [event-name {:keys [activity-uuid
                                    questionnaire-type
                                    questionnaire-uuid
                                    question-context
                                    on-success]}]]
  {:db db
   :request
   {:name event-name
    :method :post
    :uri (base-uri db (question-contexts-path
                       activity-uuid
                       (if (= questionnaire-type :floating)
                         questionnaire-uuid questionnaire-type)))
    :params question-context
    :format :json
    :response-format :json
    :headers (add-auth db)

    :on-success
    [:questionnaires/create-question-context-success
     {:questionnaire-type questionnaire-type
      :questionnaire-uuid questionnaire-uuid
      :on-success on-success}]

    :on-failure
    [:questionnaires/create-question-context-failure]}

   :dispatch-n
   [[:analytics/save-datapoint {:event event-name
                                :event-version 0}]]})

(kyber-event-fx :questionnaires/create-question-context create-question-context)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; update-question-context
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-update-question-context-success
    {:description ""
     :changelog
     [{:date "28MAR19"
       :event-version 0
       :data {}}]}))

(defn update-question-context-success
  [{:keys [db]} [event-name
                 {:keys [questionnaire-type
                         questionnaire-uuid
                         on-success]}
                 question-context]]
  (cond-> {:db (update-questionnaire-questions
                db
                {:questionnaire-type questionnaire-type
                 :questionnaire-uuid questionnaire-uuid}
                (partial mapv
                         #(if (= (:uuid question-context) (:uuid %))
                            (merge % question-context)
                            %)))
           :dispatch-n [[:analytics/save-datapoint {:event event-name
                                                    :event-version 0}]
                        [:notifier/create-toast
                         {:message "Question updated."}]]}
    (some? on-success)
    (assoc :invoke #(on-success question-context))))

(kyber-event-fx :questionnaires/update-question-context-success
                update-question-context-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-update-question-context-failure
    {:description ""
     :changelog
     [{:date "28MAR19"
       :event-version 0
       :data {}}]}))

(defn update-question-context-failure
  [{:keys [db]} [event-name _err]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast
                 {:message (str "Failed to update your question. "
                                "Please try again.")
                  :status :error}]]})

(kyber-event-fx :questionnaires/update-question-context-failure
                update-question-context-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-update-question-context
    {:description ""
     :changelog
     [{:date "28MAR19"
       :event-version 0
       :data {}}]}))

(defn update-question-context
  [{:keys [db]} [event-name {:keys [question-context
                                    questionnaire-type
                                    questionnaire-uuid
                                    activity-uuid
                                    on-success]}]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :request
   {:name event-name
    :method :patch
    :uri (base-uri db (question-contexts-path
                       activity-uuid
                       (if (= questionnaire-type :floating)
                         questionnaire-uuid questionnaire-type)
                       (:uuid question-context)))
    :params question-context
    :format :json
    :response-format :json
    :headers (add-auth db)
    :on-success
    [:questionnaires/update-question-context-success
     (cond-> {:questionnaire-type questionnaire-type
              :questionnaire-uuid questionnaire-uuid
              :on-success on-success})]
    :on-failure
    [:questionnaires/update-question-context-failure]}})

(kyber-event-fx :questionnaires/update-question-context
                update-question-context)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; delete-question-context
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-delete-question-context-success
    {:description "Question context deleted"
     :changelog
     [{:date "28MAR19"
       :event-version 0
       :data {}}]}))

(defn delete-question-context-success
  [{:keys [db]}
   [event-name {:keys [questionnaire-type
                       questionnaire-uuid
                       question-context-uuid]} _]]
  {:db (update-questionnaire-questions
        db
        {:questionnaire-uuid questionnaire-uuid
         :questionnaire-type questionnaire-type}
        (partial filterv
                 #(not= (:uuid %)
                        question-context-uuid)))
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast
                 {:message "Question removed."}]]})

(kyber-event-fx :questionnaires/delete-question-context-success
                delete-question-context-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-delete-question-context-failure
    {:description "Question context failed to delete"
     :changelog
     [{:date "28MAR19"
       :event-version 0
       :data {}}]}))

(defn delete-question-context-failure
  [{:keys [db]} [event-name _err]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast
                 {:message (str "Failed to delete your question. "
                                "Please try again.")
                  :status :error}]]})

(kyber-event-fx :questionnaires/delete-question-context-failure
                delete-question-context-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-delete-question-context
    {:description ""
     :changelog
     [{:date "28MAR19"
       :event-version 0
       :data {}}]}))

(defn delete-question-context
  [{:keys [db]} [event-name {:keys [question-context-uuid
                                    questionnaire-type
                                    questionnaire-uuid
                                    activity-uuid]}]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :request
   {:name event-name
    :method :delete
    :uri (base-uri db (question-contexts-path
                       activity-uuid
                       (if (= questionnaire-type :floating)
                         questionnaire-uuid questionnaire-type)
                       question-context-uuid))
    :format :json
    :response-format :json
    :params {}
    :headers (add-auth db)
    :on-success
    [:questionnaires/delete-question-context-success
     {:questionnaire-type questionnaire-type
      :questionnaire-uuid questionnaire-uuid
      :question-context-uuid question-context-uuid}]
    :on-failure [:questionnaires/delete-question-context-failure]}})

(kyber-event-fx :questionnaires/delete-question-context
                delete-question-context)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; adjsut-question-sort-order
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-adjust-question-sort-order-success
    {:description "Questionnaire questions have been re-ordered"
     :changelog
     [{:date "21MAR19"
       :event-version 0
       :data {}}]}))

(defn adjust-question-sort-order-success
  [{:keys [db]} [event-name
                 {:keys [questionnaire-type questionnaire-uuid]}
                 questionnaire]]
  {:db (assoc-in db
                 (if (= questionnaire-type :floating)
                   [:questionnaires questionnaire-type questionnaire-uuid]
                   [:questionnaires questionnaire-type])
                 questionnaire)
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast
                 {:message "Questions re-ordered"}]]})

(kyber-event-fx :questionnaires/adjust-question-sort-order-success
                adjust-question-sort-order-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-adjust-question-sort-order-failure
    {:description "Questionnaire questions have NOT been re-ordered"
     :changelog
     [{:date "21MAR19"
       :event-version 0
       :data {}}]}))

(defn adjust-question-sort-order-failure
  [{:keys [db]} [event-name _]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast
                 {:message "Failed to re-order questions."
                  :status :error}]]})

(kyber-event-fx :questionnaires/adjust-question-sort-order-failure
                adjust-question-sort-order-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-adjust-question-sort-order
    {:description "Request to re-ordered questionnaire questions"
     :changelog
     [{:date "21MAR19"
       :event-version 0
       :data {}}]}))

(defn adjust-question-sort-order
  [{:keys [db]} [event-name {:keys [questionnaire-type
                                    questionnaire-uuid
                                    question-start-idx
                                    question-end-idx
                                    activity-uuid]}]]
  (let [previous-questionnaire
        (get-in db (if (= questionnaire-type :floating)
                     [:questionnaires questionnaire-type questionnaire-uuid]
                     [:questionnaires questionnaire-type]))

        updated-questionnaire
        (update previous-questionnaire :questions (partial au/reorder-coll
                                                           question-start-idx
                                                           question-end-idx))]
    {:db db
     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0}]]
     :request
     {:name event-name
      :method :patch
      :uri (base-uri db (question-order-path
                         activity-uuid
                         (if (= questionnaire-type :floating)
                           questionnaire-uuid questionnaire-type)))
      :params {:question-contexts (mapv :uuid
                                        (:questions updated-questionnaire))}
      :format :json
      :response-format :json
      :headers (add-auth db)
      :on-success
      [:questionnaires/adjust-question-sort-order-success
       {:questionnaire-type questionnaire-type
        :questionnaire-uuid questionnaire-uuid}]
      :on-failure [:questionnaires/adjust-question-sort-order-failure]}}))

(kyber-event-fx :questionnaires/adjust-question-sort-order
                adjust-question-sort-order)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; questionnaire-question-remove-resource
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-questionaire-remove-question-resources
    {:description (str "Prompts a user with the option to remove a "
                       "questionnaire question")
     :changelog
     [{:date "19MAR19"
       :event-version 0
       :data {}}]}))

(defn questionnaire-question-remove-resource
  [{:keys [db]} [event-name {:keys [questionnaire-type
                                    questionnaire-uuid
                                    question-context-uuid
                                    resource-uuid
                                    activity-uuid]}]]
  {:db db
   :dispatch-n
   [[:notifier/create-alert
     {:title "Remove resource from the question?"
      :message (str "Are you sure you want to remove this "
                    "resource from the question?")
      :confirm-action
      (fn []
        (re-frame/dispatch
         [:questionnaires/update-question-context
          {:questionnaire-type questionnaire-type
           :questionnaire-uuid questionnaire-uuid
           :activity-uuid activity-uuid
           :should-transition false
           :question-context
           {:uuid question-context-uuid
            :resources (->> (get-in db [:questionnaires
                                        questionnaire-type
                                        :questions])
                            (filter #(= question-context-uuid (:uuid %)))
                            (first)
                            :resources
                            (filterv #(not= resource-uuid (:uuid %))))}}]))
      :confirm-text "Remove resource"
      :deny-text "Keep Resource"}]
    [:analytics/save-datapoint {:event event-name
                                :event-version 0}]]})

(kyber-event-fx :questionnaires/questionnaire-question-remove-resource
                questionnaire-question-remove-resource)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; questionnaire-question-add-resource
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-questionaire-question-add-resource
    {:description (str "Checks if a resources can be added to a question"
                       ", then warns or kicks of an event to request the "
                       "update to add the resource.")
     :changelog
     [{:date "19MAR19"
       :event-version 0
       :data {}}]}))

(defn questionnaire-question-add-resource
  [{:keys [db]} [event-name {:keys [questionnaire-type
                                    questionnaire-uuid
                                    activity-uuid
                                    curriculum-uuid
                                    question-context-uuid
                                    learning-objective
                                    resource-uuid]
                             :as props}]]
  (let [question-resources (if (= :floating questionnaire-type)
                             (->> (get-in db [:questionnaires
                                              questionnaire-type
                                              questionnaire-uuid
                                              :questions])
                                  (filterv #(= question-context-uuid
                                               (:uuid %)))
                                  (first)
                                  :resources)
                             (->> (get-in db [:questionnaires
                                              questionnaire-type
                                              :questions])
                                  (filterv #(= question-context-uuid
                                               (:uuid %)))
                                  (first)
                                  :resources))
        activity-resource (get-in db [:activity-resources resource-uuid])
        is-new (and (some? activity-uuid)
                    (some? curriculum-uuid)
                    (nil? activity-resource))
        exists-in-question (->> question-resources
                                (filter #(= resource-uuid (:uuid %)))
                                (first)
                                (some?))]
    {:db db
     :dispatch-n
     (cond-> [[:analytics/save-datapoint {:event event-name
                                          :event-version 0}]]
       (and (false? is-new)
            (some? activity-resource)
            (false? exists-in-question))
       (conj [:questionnaires/update-question-context
              {:questionnaire-type
               questionnaire-type

               :questionnaire-uuid
               questionnaire-uuid

               :activity-uuid
               activity-uuid

               :should-transition
               false

               :question-context
               {:uuid question-context-uuid
                :resources (conj question-resources activity-resource)}}])

       (and (nil? activity-resource)
            (false? is-new))
       (conj [:notifier/create-toast
              {:message (str "Oh no! We're having trouble adding your "
                             "resource, please try again.")
               :status :error}])

       (true? is-new)
       (conj [:activity-resources/create-activity-resource
              (cond-> {:activity-uuid
                       activity-uuid

                       :curriculum-uuid
                       curriculum-uuid

                       :resource
                       resource-uuid

                       :on-success
                       #(re-frame/dispatch
                         [:questionnaires/questionnaire-question-add-resource
                          props])}
                (some? learning-objective)
                (assoc :learning-objective learning-objective))]))}))

(kyber-event-fx :questionnaires/questionnaire-question-add-resource
                questionnaire-question-add-resource)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; questionnaire-question-add-question-hover
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-questionaire-add-question-hover-idx
    {:description (str "Tags a question context with is-hovering "
                       "when being hovered.")
     :changelog
     [{:date "29MAY19"
       :event-version 0
       :data {}}]}))


(defn add-question-hover-idx
  [{:keys [db]} [event-name {:keys [activity-uuid
                                    questionnaire-type
                                    questionnaire-uuid
                                    question-idx]}]]
  (let [{assessment-questions :assessment/questions}
        (kyber-db/get-record db :assessment
                             (assessment/->ident-key {:activity/uuid activity-uuid}))

        {questionnaire-questions :questionnaire/questions}
        (kyber-db/get-record db :questionnaire questionnaire-uuid)]
    {:db (if (= questionnaire-type :assessments)
           (kyber-db/tx-record db
                               {:ident-key (assessment/->ident-key {:activity/uuid activity-uuid})
                                :model :assessment}
                               {:assessment/questions (vec
                                                       (map-indexed #(if (= %1 question-idx)
                                                                       (assoc %2 :is-hovering true)
                                                                       (assoc %2 :is-hovering false))
                                                                    assessment-questions))})
           (kyber-db/tx-record db
                               {:ident-key questionnaire-uuid
                                :model :questionnaire}
                               {:questionnaire/questions (vec
                                                          (map-indexed #(if (= %1 question-idx)
                                                                          (assoc %2 :is-hovering true)
                                                                          (assoc %2 :is-hovering false))
                                                                       questionnaire-questions))}))


     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0}]]}))

(kyber-event-fx ::add-question-hover-idx add-question-hover-idx)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; questionnaire-question-clear-drag-state
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-questions-clear-drag-state
    {:description (str "Clears all drag state from question contexts")
     :changelog
     [{:date "29MAY19"
       :event-version 0
       :data {}}]}))

(defn questions-clear-drag-state
  [{:keys [db]} [event-name {:keys [activity-uuid questionnaire-uuid questionnaire-type]}]]
  (let [{assessment-questions :assessment/questions}
        (kyber-db/get-record db :assessment (assessment/->ident-key {:activity/uuid activity-uuid}))

        {questionnaire-questions :questionnaire/questions}
        (kyber-db/get-record db :questionnaire questionnaire-uuid)]
    {:db (if (= questionnaire-type :assessments)
           (kyber-db/tx-record db
                               {:ident-key (assessment/->ident-key {:activity/uuid activity-uuid})
                                :model :assessment}
                               {:assessment/questions (mapv #(assoc % :is-hovering nil) assessment-questions)})
           (kyber-db/tx-record db
                               {:ident-key questionnaire-uuid
                                :model :questionnaire}
                               {:questionnaire/questions (mapv #(assoc % :is-hovering nil) questionnaire-questions)}))


     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0}]]}))

(kyber-event-fx ::questions-clear-drag-state questions-clear-drag-state)
